import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MetricWidget } from "@/components/MetricWidget";
import { LeadForm } from "@/components/LeadForm";
import {
  DashboardSkeleton,
  DashboardEmptyState,
  WinRateChart,
  ActivitiesTimeline,
  PipelineTooltip,
  DeltaBadge,
} from "@/components/DashboardWidgets";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, PoundSterling, TrendingUp, Target, Plus,
  Building2, ArrowRight, Clock, ShieldAlert
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const stageLabels = {
  new: "New", contacted: "Contacted", qualified: "Qualified",
  proposal: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost"
};

const stageColors = {
  new: "#3b82f6", contacted: "#6366f1", qualified: "#8b5cf6",
  proposal: "#eab308", negotiation: "#f97316", won: "#10b981", lost: "#ef4444"
};

const sourceLabels = {
  website: "Website", phone: "Phone", referral: "Referral",
  "walk-in": "Walk-in", email: "Email", trade_show: "Trade Show", social_media: "Social Media"
};

const PIE_COLORS = ["#3b82f6", "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#f97316"];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCreateLead = async (data) => {
    try {
      await api.post("/leads", data);
      toast.success("Lead created successfully");
      setShowForm(false);
      fetchStats();
    } catch (err) {
      toast.error("Failed to create lead");
    }
  };

  if (loading) return <DashboardSkeleton />;

  const isEmpty = !stats?.total_leads || stats.total_leads === 0;

  const pipelineChartData = Object.entries(stats?.pipeline_counts || {})
    .filter(([k]) => k !== "won" && k !== "lost")
    .map(([key, count]) => ({
      name: stageLabels[key] || key,
      count,
      value: stats?.pipeline_values?.[key] || 0,
      fill: stageColors[key],
    }));

  const sourceChartData = Object.entries(stats?.source_counts || {})
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ name: sourceLabels[key] || key, value }));

  const wonCount = stats?.pipeline_counts?.won || 0;
  const lostCount = stats?.pipeline_counts?.lost || 0;
  const activeCount = Object.entries(stats?.pipeline_counts || {})
    .filter(([k]) => k !== "won" && k !== "lost")
    .reduce((sum, [, v]) => sum + v, 0);

  const conversionRate = stats?.conversion_rate || 0;
  const wonValue = stats?.won_value || 0;

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
        <div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            Commercial gas engineering pipeline overview
          </p>
        </div>
        <Button
          data-testid="dashboard-add-lead"
          onClick={() => setShowForm(true)}
          className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-5"
        >
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8" data-testid="metrics-row">
        <div className="animate-fade-slide-up" style={{ animationDelay: "0ms" }}>
          <MetricWidget title="Total Leads" value={stats?.total_leads || 0} icon={Users} />
        </div>
        <div className="animate-fade-slide-up" style={{ animationDelay: "80ms" }}>
          <MetricWidget
            title="Pipeline Value"
            value={`£${(stats?.total_pipeline_value || 0).toLocaleString()}`}
            icon={PoundSterling}
          />
        </div>
        <div className="animate-fade-slide-up" style={{ animationDelay: "160ms" }}>
          <MetricWidget
            title="Won Revenue"
            value={`£${wonValue.toLocaleString()}`}
            icon={TrendingUp}
            subtitle={wonValue > 0 ? <DeltaBadge value={conversionRate} /> : undefined}
          />
        </div>
        <div className="animate-fade-slide-up" style={{ animationDelay: "240ms" }}>
          <MetricWidget
            title="Conversion Rate"
            value={`${conversionRate}%`}
            icon={Target}
            subtitle={conversionRate > 0 ? <DeltaBadge value={conversionRate} /> : undefined}
          />
        </div>
      </div>

      {isEmpty ? (
        <DashboardEmptyState onAddLead={() => setShowForm(true)} />
      ) : (
        <>
          {/* Charts row — pipeline + source */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            <Card className="lg:col-span-2 border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "320ms" }}>
              <CardHeader className="pb-2 px-4 md:px-6">
                <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground">
                  Pipeline by Stage
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 md:px-6" data-testid="pipeline-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={pipelineChartData} barSize={28}>
                    <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
                    <Tooltip content={<PipelineTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={true}>
                      {pipelineChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "400ms" }}>
              <CardHeader className="pb-2 px-4 md:px-6">
                <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground">
                  Leads by Source
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 md:px-6" data-testid="source-chart">
                {sourceChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={sourceChartData}
                        cx="50%" cy="50%"
                        innerRadius={40} outerRadius={70}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {sourceChartData.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "#121215", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", fontSize: "12px" }}
                        itemStyle={{ color: "#a1a1aa" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {sourceChartData.map((s, i) => (
                    <div key={s.name} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-[10px] text-muted-foreground">{s.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Win Rate + Recent Leads */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            <WinRateChart wonCount={wonCount} lostCount={lostCount} activeCount={activeCount} />

            <Card className="lg:col-span-2 border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "480ms" }}>
              <CardHeader className="pb-2 px-4 md:px-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground">
                    Recent Leads
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs text-blue-400 hover:text-blue-300" asChild>
                    <a href="/leads">View All <ArrowRight className="w-3 h-3 ml-1" /></a>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 md:px-6" data-testid="recent-leads">
                {stats?.recent_leads?.length > 0 ? (
                  <div className="space-y-2 md:space-y-3">
                    {stats.recent_leads.map((lead) => (
                      <div
                        key={lead.id}
                        data-testid={`recent-lead-${lead.id}`}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-sm border border-white/5 hover:bg-white/[0.03] transition-colors duration-150 gap-2"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{lead.company_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{lead.contact_name} &middot; {lead.service_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-11 sm:ml-0">
                          <Badge variant="outline" className="text-[10px] rounded-sm">
                            {stageLabels[lead.stage] || lead.stage}
                          </Badge>
                          <span className="text-xs font-semibold text-amber-400">
                            £{Number(lead.estimated_value).toLocaleString()}
                          </span>
                          <div className="hidden sm:flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {new Date(lead.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-sm text-muted-foreground">
                    No leads yet. Create your first lead to get started.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Activities + Expiring Certs */}
          {((stats?.upcoming_activities?.length > 0) || (stats?.expiring_certificates?.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 mt-3 md:mt-4">

              <Card className="border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "560ms" }}>
                <CardHeader className="pb-2 px-4 md:px-6">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Due This Week
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="text-xs text-blue-400 hover:text-blue-300" asChild>
                      <a href="/activities">View All <ArrowRight className="w-3 h-3 ml-1" /></a>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6">
                  <ActivitiesTimeline activities={stats?.upcoming_activities} />
                </CardContent>
              </Card>

              <Card className="border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "640ms" }}>
                <CardHeader className="pb-2 px-4 md:px-6">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                      Certs Expiring Soon
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="text-xs text-blue-400 hover:text-blue-300" asChild>
                      <a href="/certificates">View All <ArrowRight className="w-3 h-3 ml-1" /></a>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-4 md:px-6">
                  {stats?.expiring_certificates?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.expiring_certificates.map(cert => {
                        const days = Math.ceil((new Date(cert.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                        const isExpired = days < 0;
                        return (
                          <div key={cert.id} className="flex items-center gap-3 p-2.5 rounded-sm border border-white/5 hover:bg-white/[0.02]">
                            <div className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${isExpired ? "bg-red-500/10" : "bg-amber-500/10"}`}>
                              <ShieldAlert className={`w-3.5 h-3.5 ${isExpired ? "text-red-400" : "text-amber-400"}`} strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-foreground truncate">{cert.cert_type}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{cert.company_name}</p>
                            </div>
                            <span className={`text-[10px] flex-shrink-0 font-medium ${isExpired ? "text-red-400" : "text-amber-400"}`}>
                              {isExpired ? `${Math.abs(days)}d ago` : `${days}d left`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-6 text-center text-xs text-muted-foreground">No certificates expiring soon</div>
                  )}
                </CardContent>
              </Card>

            </div>
          )}
        </>
      )}

      <LeadForm open={showForm} onOpenChange={setShowForm} onSubmit={handleCreateLead} />
    </div>
  );
}
